import { DownloadOutlined, EyeOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Empty, Flex, Modal, Segmented, Typography, Upload } from 'antd';
import { RcFile, UploadFile, UploadProps } from 'antd/es/upload';
import { UploadFileStatus } from 'antd/es/upload/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationInfoSelect } from 'src/components/v1/Select';
import urlPath from 'src/constants/urlPath';
import { useUpdateSearchParams } from 'src/hooks/useUpdateSearchParams';
import { ModeType } from 'src/pages/v2/UserGuide/UserGuides/UserGuides';
import { showNotification } from 'src/utils/notification';
import usePermission from 'src/hooks/usePermission';
import './AttachmentUpload.scss';
const { Text } = Typography;

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  onSubmit: (fileList: UploadFile<File>[], isForManager: ModeType, applicationInfoId: number) => void;
};

const MAX_FILE_SIZE = 4;
const FILE_ACCEPTS = 'image/*,.txt,.doc,.docx,.xlsx,.pdf,.csv';

export default function AttachmentUploadModal({ isOpen, setIsOpen, isLoading, onSubmit }: Props) {
  const { t } = useTranslation();
  const { searchParams } = useUpdateSearchParams();
  const [fileList, setFileList] = useState<UploadFile<File>[]>([]);
  const [mode, setMode] = useState<ModeType>(searchParams.get('mode') as ModeType);
  const [applicationInfoId, setApplicationInfoId] = useState<number>(Number(searchParams.get('applicationInfoId')));
  const { isAdmin } = usePermission({ path: urlPath.userGuide.location });

  const beforeUpload = (file: RcFile) => {
    const isLt5M = file.size / (1024 * 1024) > 15;

    if (isLt5M) {
      showNotification(`${file.name}: ${t('notification.warning.overCapacity')}`, 'warning');
    }
    if (file.size <= 0) {
      showNotification(t('notification.warning.emptyFile'), 'warning');
    }

    const check = fileList.some(
      (item) => item.name === file.name && item.size === file.size && item.type === file.type
    );
    if (check) {
      showNotification(t(`File: '${file.name}' đã tồn tại!`), 'warning');
    }

    return (check || file.size <= 0 || isLt5M) && Upload.LIST_IGNORE;
  };

  const handleChange: UploadProps['onChange'] = ({ fileList }) => {
    const newFileList = fileList.map((item) => ({
      ...item,
      status: 'done' as UploadFileStatus
    }));
    setFileList(newFileList);
  };

  return (
    <Modal
      className='attachment-upload-modal'
      open={isOpen}
      title='Tải lên tệp đính kèm'
      zIndex={1050}
      footer={null}
      onCancel={() => setIsOpen(false)}
    >
      <Flex vertical gap={20}>
        {isAdmin && (
          <Flex justify='center' align='center' gap={8}>
            <Segmented
              options={[
                { label: 'User', value: 'user' },
                { label: 'Admin', value: 'admin' }
              ]}
              value={mode}
              onChange={(value) => setMode(value as ModeType)}
            />
            <ApplicationInfoSelect
              showSearch={false}
              placeholder='Ứng dụng'
              style={{ width: 150 }}
              value={applicationInfoId}
              onChange={(value) => setApplicationInfoId(value as number)}
            />
          </Flex>
        )}
        <Upload
          disabled={isLoading}
          multiple
          beforeUpload={beforeUpload}
          listType='text'
          showUploadList={{
            showDownloadIcon: true,
            downloadIcon: <DownloadOutlined />,
            showRemoveIcon: true,
            showPreviewIcon: true,
            previewIcon: <EyeOutlined />
          }}
          maxCount={MAX_FILE_SIZE}
          accept={FILE_ACCEPTS}
          fileList={fileList}
          onChange={handleChange}
          // onDownload={handleDownload}
        >
          <Flex vertical className='upload-body'>
            {fileList.length > 0 && fileList.length < MAX_FILE_SIZE && (
              <Button type='dashed' icon={<UploadOutlined />} style={{ width: '100%' }}>
                {`Chọn tệp tải lên (Đã chọn: ${fileList.length})`}
              </Button>
            )}
            {(!fileList || fileList.length <= 0) && (
              <Empty
                image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
                imageStyle={{ height: 60 }}
                description={
                  <Flex vertical>
                    <Button type='link' icon={<UploadOutlined />}>
                      Chọn tệp tải lên
                    </Button>
                    <Text type='secondary'>
                      (Tải lên tối đa {MAX_FILE_SIZE} file, các loại tệp được cho phép {FILE_ACCEPTS})
                    </Text>
                  </Flex>
                }
              />
            )}
          </Flex>
        </Upload>
      </Flex>
      <Flex className='btn-rows' justify='flex-end' gap={10}>
        <Button
          type='primary'
          icon={<SaveOutlined />}
          onClick={() => {
            if (isAdmin) {
              onSubmit(fileList, mode, applicationInfoId);
            } else {
              onSubmit(fileList, 'user', applicationInfoId);
            }
          }}
          loading={isLoading}
          disabled={fileList.length <= 0}
        >
          Lưu
        </Button>
      </Flex>
    </Modal>
  );
}
